import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AdaptiveHomeSection } from '../components/AdaptiveHomeSection';
import { IkhlasFullBlock } from '../components/IkhlasFullBlock';
import { HomeOnboardingBlock } from '../components/HomeOnboardingBlock';
import { Shell } from '../components/Shell';
import './LandingPage.css';
import { CHIP_ORDER, CLUSTER_COPY, CLUSTER_SCENE_BACKGROUNDS, type ClusterKey } from './exploreClusters';
import exploreWatercolorBackground from '../assets/explore-watercolor-bg.png';

const difficultQuestionsJourneyImage = new URL('../../../../../images/difficult questions about islam.jpg', import.meta.url).toString();
const muslimsSeekingClarityJourneyImage = new URL('../../../../../images/muslims_seeking_clarity.webp', import.meta.url).toString();
const foundationsOfFaithJourneyImage = new URL('../../../../../images/foundations of faith.jpg', import.meta.url).toString();
const reasonScienceAndHumanNatureJourneyImage = new URL('../../../../../images/Reason, Science, and Human Nature.webp', import.meta.url).toString();
const returningAfterDoubtJourneyImage = new URL('../../../../../images/returning from doubts.webp', import.meta.url).toString();
const quranJourneyImage = new URL('../../../../../images/quran.webp', import.meta.url).toString();
const worshipAndThePillarsJourneyImage = new URL('../../../../../images/worship.jpg', import.meta.url).toString();

function useInfiniteCarouselControls<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const scrollBy = (dir: -1 | 1) => {
    const el = ref.current;
    if (!el) return;
    const dx = Math.min(520, Math.max(320, el.clientWidth * 0.75)) * dir;
    el.scrollBy({ left: dx, behavior: 'smooth' });
  };

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Start in the middle third so we can “wrap” in both directions.
    const jump = () => {
      el.scrollLeft = el.scrollWidth / 3;
    };
    requestAnimationFrame(jump);

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const w = el.scrollWidth / 3;
        if (w <= 0) return;
        if (el.scrollLeft < w * 0.35) el.scrollLeft += w;
        if (el.scrollLeft > w * 1.65) el.scrollLeft -= w;
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, []);

  return { ref, prev: () => scrollBy(-1), next: () => scrollBy(1) };
}

const JOURNEY_CARD_IMAGES: Record<string, string> = {
  'difficult-questions-about-islam': difficultQuestionsJourneyImage,
  'for-muslims-seeking-clarity': muslimsSeekingClarityJourneyImage,
  'foundations-of-faith': foundationsOfFaithJourneyImage,
  'reason-science-and-human-nature': reasonScienceAndHumanNatureJourneyImage,
  'returning-after-doubt': returningAfterDoubtJourneyImage,
  'the-quran-journey': quranJourneyImage,
  'worship-and-the-pillars': worshipAndThePillarsJourneyImage,
};

const entryPaths = [
  {
    title: "I'm curious about Islam",
    body: 'Start with clear foundations, then move into what interests you most.',
    to: '/m/who-is-allah',
    cue: 'Curiosity',
  },
  {
    title: 'I have difficult questions',
    body: 'Enter through serious questions without pressure or defensiveness.',
    to: '/journeys/difficult-questions-about-islam',
    cue: 'Depth',
  },
  {
    title: "I'm comparing beliefs",
    body: 'Explore side-by-side themes with intellectual discipline and fairness.',
    to: '/m/what-does-islam-say-about-jesus',
    cue: 'Compare',
  },
  {
    title: "I'm Muslim and want clarity",
    body: 'Find grounding pathways for doubt, confusion, and deeper understanding.',
    to: '/journeys/for-muslims-seeking-clarity',
    cue: 'Clarity',
  },
  {
    title: 'I want a guided path',
    body: 'Follow structured learning journeys with calm progression and continuity.',
    to: '/journeys',
    cue: 'Guided',
  },
  {
    title: "I know what I'm looking for",
    body: 'Browse the full library by topic and move directly to specific modules.',
    to: '/browse',
    cue: 'Direct',
  },
];

const featuredQuestions = [
  { title: 'Who is Allah?', slug: 'who-is-allah', kind: 'Foundational', level: 'Core', read: '8 min' },
  {
    title: 'Why Islam instead of another religion?',
    slug: 'why-islam-instead-of-another-religion',
    kind: 'Comparative',
    level: 'Core',
    read: '12 min',
  },
  { title: 'Who was Prophet Muhammad?', slug: 'who-was-prophet-muhammad', kind: 'Foundational', level: 'Core', read: '10 min' },
  { title: "Is the Qur'an preserved?", slug: 'is-the-quran-preserved', kind: 'Scripture', level: 'Core', read: '11 min' },
  {
    title: 'What does Islam say about Jesus?',
    slug: 'what-does-islam-say-about-jesus',
    kind: 'Comparative',
    level: 'Core',
    read: '12 min',
  },
  {
    title: 'Why does Islam emphasize worship?',
    slug: 'why-does-islam-emphasize-worship',
    kind: 'Foundational',
    level: 'Intro',
    read: '9 min',
  },
  {
    title: 'Why is there suffering in the world?',
    slug: 'why-is-there-suffering-in-the-world',
    kind: 'Difficult',
    level: 'Intermediate',
    read: '13 min',
  },
  {
    title: 'What about free will and predestination?',
    slug: 'what-about-free-will-and-predestination',
    kind: 'Difficult',
    level: 'Advanced',
    read: '14 min',
  },
  {
    title: 'What does Islam say about women?',
    slug: 'what-does-islam-say-about-women',
    kind: 'Difficult',
    level: 'Intermediate',
    read: '12 min',
  },
  { title: 'What about apostasy?', slug: 'what-about-apostasy-in-islam', kind: 'Difficult', level: 'Advanced', read: '15 min' },
  {
    title: 'Why are there different schools of thought?',
    slug: 'why-are-there-different-schools-of-thought',
    kind: 'Historical',
    level: 'Intermediate',
    read: '12 min',
  },
  {
    title: 'Can faith and reason coexist in Islam?',
    slug: 'how-does-islam-compare-faith-and-reason',
    kind: 'Comparative',
    level: 'Intermediate',
    read: '11 min',
  },
];

const journeys = [
  {
    title: 'Exploring Islam for the First Time',
    slug: 'exploring-islam-for-the-first-time',
    for: 'Curious first-time visitors',
    steps: 7,
    pace: '2-3 hours',
    body: 'A calm sequence that starts with core meaning and builds confidence steadily.',
  },
  {
    title: 'Christian Questions About Islam',
    slug: 'christian-questions-about-islam',
    for: 'Christian readers and comparativists',
    steps: 8,
    pace: '3-4 hours',
    body: 'A careful comparative path around Jesus, scripture, salvation, and theology.',
  },
  {
    title: "Understanding the Qur'an",
    slug: 'the-quran-journey',
    for: 'Readers focused on scripture',
    steps: 6,
    pace: '2 hours',
    body: 'A structured path through revelation, preservation, interpretation, and purpose.',
  },
  {
    title: 'Islam and Difficult Questions',
    slug: 'difficult-questions-about-islam',
    for: 'Skeptical and critical users',
    steps: 9,
    pace: '3-4 hours',
    body: 'A direct but balanced route through the hardest topics with method and context.',
  },
  {
    title: 'Foundations of Faith',
    slug: 'foundations-of-faith',
    for: 'Learners who want grounding',
    steps: 8,
    pace: '3 hours',
    body: 'A coherent sequence across God, prophethood, worship, meaning, and hereafter.',
  },
  {
    title: 'For Muslims Seeking Clarity',
    slug: 'for-muslims-seeking-clarity',
    for: 'Muslims navigating doubts or confusion',
    steps: 7,
    pace: '2-3 hours',
    body: 'A reflective journey for Muslims who want structured, honest, and stable learning.',
  },
];

function HomeJourneysCarousel({ items }: { items: typeof journeys }) {
  const { ref, prev, next } = useInfiniteCarouselControls<HTMLDivElement>();
  const looped = useMemo(() => [...items, ...items, ...items], [items]);
  return (
    <div className="wi-home-carousel" aria-label="Journeys carousel">
      <div className="wi-home-carousel-row" aria-label="Carousel">
        <button
          type="button"
          className="wi-home-carousel-arrow wi-home-carousel-arrow--left"
          onClick={(e) => (e.stopPropagation(), prev())}
          aria-label="Previous"
        >
          ‹
        </button>
        <div ref={ref} className="wi-home-carousel-track" role="list" onClick={(e) => e.stopPropagation()}>
        {looped.map((j, idx) => {
          const img = JOURNEY_CARD_IMAGES[j.slug] ?? exploreWatercolorBackground;
          return (
            <Link
              key={`${j.slug}-${idx}`}
              to={`/journeys/${j.slug}`}
              className="wi-home-carousel-card"
              role="listitem"
              style={{ ['--wi-card-bg' as any]: `url(${img})` }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="wi-home-carousel-card__veil" aria-hidden />
              <p className="wi-home-carousel-card__kicker">{j.for}</p>
              <h3 className="wi-home-carousel-card__title">{j.title}</h3>
              <p className="wi-home-carousel-card__meta">
                {j.steps} steps · {j.pace}
              </p>
            </Link>
          );
        })}
        </div>
        <button
          type="button"
          className="wi-home-carousel-arrow wi-home-carousel-arrow--right"
          onClick={(e) => (e.stopPropagation(), next())}
          aria-label="Next"
        >
          ›
        </button>
      </div>
    </div>
  );
}

function HomeBigIdeasCarousel() {
  const { ref, prev, next } = useInfiniteCarouselControls<HTMLDivElement>();
  const items = useMemo(() => CHIP_ORDER.slice(0, 7), []);
  const looped = useMemo(() => [...items, ...items, ...items], [items]);
  return (
    <div className="wi-home-carousel" aria-label="Big Ideas clusters carousel">
      <div className="wi-home-carousel-row" aria-label="Carousel">
        <button
          type="button"
          className="wi-home-carousel-arrow wi-home-carousel-arrow--left"
          onClick={(e) => (e.stopPropagation(), prev())}
          aria-label="Previous"
        >
          ‹
        </button>
        <div ref={ref} className="wi-home-carousel-track" role="list" onClick={(e) => e.stopPropagation()}>
        {looped.map((k: ClusterKey, idx) => {
          const copy = CLUSTER_COPY[k];
          const img = CLUSTER_SCENE_BACKGROUNDS[k] ?? exploreWatercolorBackground;
          return (
            <Link
              key={`${k}-${idx}`}
              to={`/explore#wi-explore-cluster-${k}`}
              className="wi-home-carousel-card wi-home-carousel-card--cluster"
              role="listitem"
              style={{ ['--wi-card-bg' as any]: `url(${img})` }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="wi-home-carousel-card__veil" aria-hidden />
              <p className="wi-home-carousel-card__kicker">Cluster</p>
              <h3 className="wi-home-carousel-card__title">{copy.title}</h3>
              <p className="wi-home-carousel-card__desc">{copy.blurb}</p>
            </Link>
          );
        })}
        </div>
        <button
          type="button"
          className="wi-home-carousel-arrow wi-home-carousel-arrow--right"
          onClick={(e) => (e.stopPropagation(), next())}
          aria-label="Next"
        >
          ›
        </button>
      </div>
    </div>
  );
}

const howItWorks = [
  { title: 'Start with your questions', body: 'Begin with what is genuinely on your mind.' },
  { title: 'Choose how deep to go', body: 'Move from concise orientation into deeper context at your pace.' },
  { title: 'Follow a shaped path', body: 'Continue into modules and journeys that fit your concerns and intent.' },
];

const methodPrinciples = [
  'Difficult questions are addressed directly, not bypassed.',
  'Complex issues are presented with context, not slogans.',
  'Where scholarship differs, perspectives are acknowledged clearly.',
  'Framing, evidence, and response are separated for honest inquiry.',
];

const foundationalPreview = [
  { title: 'God and Oneness', slug: 'what-is-tawhid' },
  { title: 'Prophethood', slug: 'what-is-prophethood' },
  { title: "The Qur'an", slug: 'what-is-the-quran' },
  { title: 'Prayer and Worship', slug: 'what-is-prayer-in-islam' },
  { title: 'Purpose of Life', slug: 'what-is-the-purpose-of-life-in-islam' },
  { title: 'The Hereafter', slug: 'what-is-the-hereafter' },
];

const difficultPreview = [
  { title: 'Apostasy', slug: 'what-about-apostasy-in-islam' },
  { title: 'Women in Islam', slug: 'what-does-islam-say-about-women' },
  { title: 'Jihad and warfare', slug: 'what-does-islam-say-about-jihad' },
  { title: 'Free will and divine decree', slug: 'what-about-free-will-and-predestination' },
  { title: 'Punishment and justice', slug: 'why-are-there-legal-punishments-in-islam' },
  { title: 'Sectarian differences', slug: 'why-are-there-different-sects-in-islam' },
  { title: 'Hadith and authenticity', slug: 'can-hadith-be-trusted' },
  { title: 'Modern moral criticism', slug: 'islam-and-modern-moral-criticism' },
];

export function LandingPage() {
  const [showHomeOnboarding, setShowHomeOnboarding] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    try {
      const v = localStorage.getItem('wi_onboarding_done');
      // onboarding should run only if the user has never completed (or explicitly skipped) it.
      setShowHomeOnboarding(v == null);
    } catch {
      setShowHomeOnboarding(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    const nodes = Array.from(
      document.querySelectorAll<HTMLElement>(
        [
          '.wi-shell-home .wi-home-ikhlas-block',
          '.wi-shell-home .wi-home-onboarding-block',
          '.wi-shell-home .wi-adaptive-band--premium',
          '.wi-shell-home .wi-signal-band',
          '.wi-shell-home .landing-page-sections.wi-home-sections > section',
        ].join(',')
      )
    );
    if (!nodes.length) return;

    const shell = document.querySelector<HTMLElement>('.wi-shell-home');
    shell?.classList.add('wi-home-anim-ready');

    // Ensure anything already in the viewport is visible immediately.
    const vh = window.innerHeight || 0;
    for (const n of nodes) {
      const r = n.getBoundingClientRect();
      if (r.bottom > vh * 0.12 && r.top < vh * 0.88) n.classList.add('is-in-view');
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) e.target.classList.add('is-in-view');
        }
      },
      { threshold: 0.28, rootMargin: '-10% 0px -12% 0px' }
    );

    for (const n of nodes) observer.observe(n);
    return () => observer.disconnect();
  }, [showHomeOnboarding]);

  return (
    <Shell>
      <div className="landing-page wi-home-clean wi-home-v4 wi-home-nizam">
        <section className="wi-hero wi-band wi-band-hero" aria-labelledby="wi-home-hero-title">
          <div className="wi-section-inner wi-hero-inner">
            <div className="wi-hero-copy">
              <p className="wi-mk-kicker">Guided inquiry platform</p>
              <h1 id="wi-home-hero-title">Why Islam</h1>
              <p className="wi-hero-lede">Follow your own path while exploring Islam.</p>
              <Link to="/journeys#wi-journey-scenes" className="wi-hero-cta-pill">
                Start exploring
              </Link>
            </div>
          </div>
        </section>

        <IkhlasFullBlock />

        {showHomeOnboarding ? (
          <HomeOnboardingBlock onFinished={() => setShowHomeOnboarding(false)} />
        ) : (
          <AdaptiveHomeSection />
        )}

        <div className="landing-page-sections wi-home-sections">
          {/* Journeys */}
          <section
            className="wi-home-scene-block wi-home-scene-block--journeys"
            aria-label="Journeys"
            role="link"
            tabIndex={0}
            onClick={(e) => {
              const t = e.target as HTMLElement | null;
              if (t?.closest('a,button,input,select,textarea')) return;
              nav('/journeys');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') nav('/journeys');
            }}
          >
            <div className="wi-home-scene-veil" aria-hidden />
            <div className="wi-home-scene-layout">
              <div className="wi-home-scene-copy wi-home-scene-copy--upper">
              <p className="wi-mk-kicker">Guided paths</p>
              <h2 className="wi-home-scene-title">Journeys through Islam</h2>
              <p className="wi-home-scene-lede">
                Begin from where you are — each path is structured, patient, and complete.
              </p>
            </div>
              <HomeJourneysCarousel items={journeys} />
            </div>
          </section>

          {/* Big Ideas */}
          <section
            className="wi-home-scene-block wi-home-scene-block--big-ideas"
            aria-label="Big Ideas"
            role="link"
            tabIndex={0}
            onClick={(e) => {
              const t = e.target as HTMLElement | null;
              if (t?.closest('a,button,input,select,textarea')) return;
              nav('/explore');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') nav('/explore');
            }}
          >
            <div className="wi-home-scene-veil" aria-hidden />
            <div className="wi-home-scene-layout">
              <div className="wi-home-scene-copy wi-home-scene-copy--upper">
              <p className="wi-mk-kicker">Structured inquiry</p>
              <h2 className="wi-home-scene-title">Big Ideas</h2>
              <p className="wi-home-scene-lede">
                Move through Islam as a structured landscape — domain by domain — with curated clusters that surface depth.
              </p>
            </div>
              <HomeBigIdeasCarousel />
            </div>
          </section>

          {/* Browse */}
          <section
            className="wi-home-scene-block wi-home-scene-block--browse"
            aria-label="Browse"
            role="link"
            tabIndex={0}
            onClick={(e) => {
              const t = e.target as HTMLElement | null;
              if (t?.closest('a,button,input,select,textarea')) return;
              nav('/browse');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') nav('/browse');
            }}
          >
            <div className="wi-home-scene-veil wi-home-scene-veil--center" aria-hidden />
            <div className="wi-home-scene-layout wi-home-scene-layout--browse">
              <div className="wi-home-scene-copy">
                <p className="wi-mk-kicker">Full library</p>
                <h2 className="wi-home-scene-title">Browse</h2>
                <p className="wi-home-scene-lede">
                  Search every topic, filter by domain, and scan the open catalog — or jump into curated Big Ideas.
                </p>
                <div className="wi-home-browse-hints" aria-hidden>
                  <span className="wi-home-browse-hint">Search</span>
                  <span className="wi-home-browse-hint">Filter</span>
                  <span className="wi-home-browse-hint">Open catalog</span>
                </div>
              </div>
            </div>
          </section>

          {/* Trust and Method + How */}
          <section
            className="wi-section wi-band wi-band-method-how wi-home-scene-block wi-home-scene-block--method-how"
            aria-label="Trust, method, and how it works"
          >
            <div className="wi-home-scene-veil wi-home-scene-veil--method" aria-hidden />
            <div className="wi-home-scene-layout wi-home-scene-layout--method">
              <div className="wi-home-scene-copy wi-home-scene-copy--method">
                <p className="wi-mk-kicker">Editorial principles</p>
                <h2 className="wi-home-scene-title">Trust and method</h2>
                <p className="wi-home-scene-lede">
                  Calm tone, intellectual honesty, and structured exploration — designed to engage serious questions without defensiveness.
                </p>
              </div>

              <div className="wi-method-grid">
                <div className="wi-method-panel wi-method-panel--principles" aria-label="Trust and method principles">
                  <h3 className="wi-method-panel__title">What we commit to</h3>
                  <ul className="wi-method-list">
                    {methodPrinciples.map((principle) => (
                      <li key={principle} className="wi-method-list__item">
                        {principle}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="wi-method-panel wi-method-panel--how" aria-label="How it works">
                  <h3 className="wi-method-panel__title">How it works</h3>
                  <ol className="wi-how-steps">
                    {howItWorks.map((step, idx) => (
                      <li key={step.title} className="wi-how-step">
                        <div className="wi-how-step__num">Step {idx + 1}</div>
                        <div className="wi-how-step__body">
                          <div className="wi-how-step__title">{step.title}</div>
                          <div className="wi-how-step__desc">{step.body}</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </section>

          {/* Start Now */}
          <section
            className="wi-section wi-band wi-band-final wi-section-final wi-final-cta wi-home-scene-block wi-home-scene-block--start-now"
            aria-label="Start now"
          >
            <div className="wi-section-inner">
              <div className="wi-home-scene-copy">
                <p className="wi-kicker">Start now</p>
                <h2 className="wi-home-scene-title wi-home-scene-title--small">Begin with clarity</h2>
                <p className="wi-home-scene-lede">
                  Choose your own entry point and move at a pace that respects your questions.
                </p>
                <div className="wi-home-scene-actions">
                  <Link to="/journeys" className="button primary">
                    Begin your journey
                  </Link>
                  <Link to="/browse" className="button ghost">
                    Browse all modules
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Shell>
  );
}
